import { z } from 'zod';
/** Calendar dates retain the entered day rather than acquiring a timezone. */
export const personnelDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Enter a valid calendar date');
const text = z.string().trim().max(500).nullable().optional();
const phone = z.string().trim().min(7).max(50).nullable().optional();
const date = personnelDateSchema.nullable().optional();
/** Reused by application answers and current staff-maintained contact details. */
export const personAddressSchema = z.object({
    streetAddress: text,
    addressLine2: text,
    city: text,
    state: text,
    postalCode: text,
    country: text,
}).strict();
export const emergencyContactSchema = z.object({
    name: z.string().trim().min(1).max(200),
    relationship: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(7).max(50),
    address: text,
}).strict();
/**
 * How many emergency contacts one person holds, said once. The application asks for up to this
 * many, the profile projection accepts this many, and the person record keeps this many, so an
 * approved third contact always has a place to land (#212; it was 3, 3 and 2).
 */
export const MAX_EMERGENCY_CONTACTS = 3;
/**
 * The identity details that cross to a browser (#606). No date of birth: ADR-012 §4 gives that out
 * only through the audited reveal. Strict, so a response that carried one fails to parse.
 */
export const personIdentitySchema = z.object({
    previousName: text,
    identityVerified: z.boolean().nullable().optional(),
}).strict();
/**
 * The identity details as STORED and as WRITTEN: the plain date of birth ADR-012 §2 moves into the
 * sensitive store (#205). Until that migration runs the stored record still holds it, so the API's
 * data layer and the write path read this shape. Nothing that answers a browser does.
 */
export const storedPersonIdentitySchema = personIdentitySchema.extend({ dateOfBirth: date }).strict();
/**
 * One service area as a real place (ADR-021 section 2, #593; first ADR-018 §1, #473). `placeId` is OUR code for an entry
 * of the Virginia place list (`findVirginiaPlace`), and no name and no position: those are read from the list.
 * `checkedAt` is when it was picked.
 *
 * SHAPE ONLY, on purpose. A record saved before the list existed holds one of Google's ids here, and it must keep
 * loading, so this schema does not ask whether the code is in the list. The API asks that on every write
 * (`unlistedPlaceIssues` in `apps/api`).
 */
export const MAX_SERVICE_PLACES = 20;
export const servicePlaceSchema = z.object({
    placeId: z.string().trim().min(1).max(300),
    kind: z.enum(['city', 'zip']),
    checkedAt: z.string().datetime(),
}).strict();
export const servicePlacesSchema = z.array(servicePlaceSchema).max(MAX_SERVICE_PLACES).superRefine((places, ctx) => {
    const seen = new Set();
    places.forEach((place, index) => {
        if (seen.has(place.placeId))
            ctx.addIssue({ code: 'custom', path: [index, 'placeId'], message: 'A place can be listed once' });
        seen.add(place.placeId);
    });
});
/** Classification is separate from scheduling type (for example, W2 and PRN). */
export const employeeAssignmentSchema = z.object({
    employerName: text,
    employerResponsibility: z.enum(['vigeo', 'other']).nullable().optional(),
    assignedDuties: z.string().trim().max(2000).nullable().optional(),
    positionTitle: text,
    department: text,
    employmentClassification: z.enum(['w2', '1099']).nullable().optional(),
    employmentType: text,
    serviceArea: text,
    /** Optional: records saved before #473 have none and parse as they were. The typed `serviceArea` above is never changed by it. */
    servicePlaces: servicePlacesSchema.optional(),
    supervisorName: text,
}).strict();
export const emrStatusSchema = z.enum(['not-started', 'in-progress', 'provisioned', 'disabled']);
export const emrProvisioningSchema = z.object({
    status: emrStatusSchema.default('not-started'),
    accountIdentifier: text,
    notes: z.string().trim().max(4000).nullable().optional(),
    /** Server stamps the first provisioning; disabling an account preserves this history. */
    provisionedAt: z.string().datetime().nullable().optional(),
    provisionedBy: z.string().min(1).nullable().optional(),
}).strict();
export const emrProvisioningInputSchema = emrProvisioningSchema.omit({ provisionedAt: true, provisionedBy: true });
export const finalHrClearanceSchema = z.object({
    confirmedAt: z.string().datetime(),
    confirmedBy: z.string().min(1),
    reason: z.string().trim().min(1),
}).strict();
/** Candidate-supplied history stays on immutable application submissions, not Person. */
export const employmentHistoryEntrySchema = z.object({
    facility: text,
    positionHeld: text,
    employmentType: text,
    address: text,
    cityState: text,
    fromDate: date,
    toDate: date,
    immediateSupervisor: text,
    businessPhone: phone,
    mayContact: z.boolean().optional(),
    reasonForLeaving: z.string().trim().max(2000).nullable().optional(),
}).strict();
export const employmentReferenceSchema = z.object({
    referenceType: z.enum([
        'former_supervisor', 'employer_hr_department', 'professional_reference',
        'educational_reference', 'personal_no_prior_experience',
    ]).nullable().optional(),
    name: text,
    relationship: text,
    organization: text,
    phone,
    email: z.string().email().nullable().optional(),
    mayContact: z.boolean().optional(),
}).strict();
export const educationEntrySchema = z.object({
    institution: text,
    location: text,
    fromDate: date,
    toDate: date,
    degreeEarned: text,
}).strict();
export const candidateCredentialSchema = z.object({
    credentialType: text,
    credentialNumber: text,
    issuingState: text,
    expirationDate: date,
}).strict();
//# sourceMappingURL=details.js.map